/**
 * A calendar popup in a right-to-left page opens inward from the field's RIGHT edge (docs/design/35 section 10; the report and every date field).
 * PrimeNG positions a body-appended popup from the field's left edge, so in Arabic it spilled past the field's far side and could leave the
 * viewport. This keeps the popup on the field's right edge, and inside the viewport (clamped, so it "flips" inward at either edge). Left-to-right
 * pages keep PrimeNG's own horizontal placement, which already aligns and flips there. Vertically, in both directions: a tall popup (the date and time
 * picker) that fits neither below nor above its field would be cut off at the top or bottom of the screen, so it is moved into the viewport (it may then
 * overlap its field, which beats being clipped). Central, like the select titles: a new date field needs nothing.
 */
const PANEL = '.p-datepicker-panel';
const MARGIN = 8;
/** Re-aligns one popup may run within one frame. A real re-position by PrimeNG (month or year view, the opening animation) needs one or two. */
const MAX_REALIGNS_PER_FRAME = 8;

export function installDatepickerRtlAlignment(doc: Document): () => void {
  const win = doc.defaultView;
  if (!win) return () => undefined;

  const align = (panel: HTMLElement): void => {
    // The field that opened it: the date picker holding the focused element (a click on the field or its icon focuses the input).
    const host = (doc.activeElement as Element | null)?.closest('p-datepicker') as HTMLElement | null;
    if (!host) return;
    const box = panel.getBoundingClientRect();
    if (!box.width) return;
    // The panel's own offset parent decides what "left" and "top" are measured from (the page margin, a positioned ancestor): correct by what is actually drawn.
    if (doc.documentElement.dir === 'rtl') {
      const field = host.getBoundingClientRect();
      const viewport = doc.documentElement.clientWidth;
      const target = Math.min(Math.max(field.right - box.width, MARGIN), Math.max(viewport - box.width - MARGIN, MARGIN));
      if (Math.abs(box.left - target) >= 1) {
        const current = Number.parseFloat(panel.style.left) || 0;
        panel.style.left = `${Math.round(current + (target - box.left))}px`;
      }
    }
    const viewportHeight = doc.documentElement.clientHeight;
    const targetTop = Math.min(Math.max(box.top, MARGIN), Math.max(viewportHeight - box.height - MARGIN, MARGIN));
    if (Math.abs(box.top - targetTop) >= 1) {
      const current = Number.parseFloat(panel.style.top) || 0;
      panel.style.top = `${Math.round(current + (targetTop - box.top))}px`;
    }
  };

  const watched = new WeakSet<Element>();
  const watch = (panel: HTMLElement): void => {
    if (watched.has(panel)) return;
    watched.add(panel);
    align(panel);
    // The opening animation scales the popup (a Web Animation: no style change tells us when it ends), so the box measured now is smaller than the final one:
    // measure again once it has finished.
    win.requestAnimationFrame(() => {
      const running = panel.getAnimations?.() ?? [];
      void Promise.allSettled(running.map((animation) => animation.finished)).then(() => align(panel));
    });
    // PrimeNG writes its own left/top when the animation starts and on every re-align: follow it. Our own write fires this observer again, and it stops by itself once the popup is
    // where it should be - unless the write has no effect (a stylesheet or an extension fixing left/top) or something else moves the popup back: that would re-run in the
    // microtask queue forever and freeze the tab. So a popup gets a small budget of re-aligns per frame; past it the observer is dropped and the popup stays where it is.
    let runs = 0;
    let frameQueued = false;
    const observer = new MutationObserver(() => {
      if (!frameQueued) {
        frameQueued = true;
        win.requestAnimationFrame(() => {
          runs = 0;
          frameQueued = false;
        });
      }
      if (++runs > MAX_REALIGNS_PER_FRAME) {
        observer.disconnect();
        return;
      }
      align(panel);
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['style'] });
  };

  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(PANEL)) watch(node);
        node.querySelectorAll<HTMLElement>(PANEL).forEach(watch);
      });
    }
  });
  bodyObserver.observe(doc.body, { childList: true, subtree: true });
  return () => bodyObserver.disconnect();
}
